import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Button } from '@components/Button';
import { FileLink } from '@components/FileLink';
import { CardCheckbox } from '@components/NextForms/CardCheckbox';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import { FileArrayInput } from '@components/NextForms/FileArrayInput';
import {
  ManagementFormLabel,
  ManagementFormLabelInner,
} from '@components/NextForms/ManagementForm';
import { BlanketInsuranceFilter } from '@components/utilities/BlanketInsurance/BlanketInsuranceFilter';
import { CustomsInstructionContext } from '@components/utilities/Bookings/helpers2';
import {
  InstructionHint,
  InstructionInputContainer,
} from '@components/utilities/Bookings/instructionComponents';
import { calendarStrings } from '@data/calendar';
import { instructionStrings } from '@data/instruction';
import { useIsAdmin } from '@hooks/useIsAdmin';
import type { BlanketInsuranceBase } from '@models/next/BlanketInsurance';
import { getDecodeResult, unwrapDecodeResult } from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import Color from 'color';
import { observer } from 'mobx-react';
import { pipe } from 'remeda';

const strings = createStrings({
  ja: {
    insuranceFilesHint: '＊保険証券の写真やコピーなどをアップロードしてください。',
    individualInsuranceFilesHint: '＊D2D担当者専用記入欄です。保険証券をアップロードしてください。',
    individualInsuranceFilesClientHint: '＊D2D担当者が保険証券をアップロードしました。',
    blanketInsuranceInfo: `${instructionStrings.insuranceTags.BLANKET}情報`,
    selectBlanketInsuranceInfo: `${instructionStrings.insuranceTags.BLANKET}情報を登録`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  hint: {
    ...common.textGray700,
    ...common.smallerText,
  },
  insuranceOption: {
    ...common.flexColumn,
    justifyContent: 'center',
    paddingLeft: 40,
    width: '100%',
    height: '100%',
  },
  fileLink: {
    ...common.middleText,
    wordBreak: 'break-all',
    padding: '0.25rem 0.5rem',
    display: 'block',
    overflow: 'hidden',
    backgroundColor: theme.palette.grey[100],
  },
  filesContainer: {
    ...common.flexColumn,
    rowGap: '0.5rem',
  },
  insuranceDisplay: {
    ...common.backgroundPrimaryAlpha3,
    padding: '1rem',
    width: '100%',
  },
  insuranceDisplayTitle: {
    ...common.textBlack,
    ...common.normalText,
    ...common.fontWeight600,
  },
  insuranceDisplayGrid: {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0,1fr)',
    columnGap: 26,
    rowGap: 6,
  },
  split: {
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    borderTop: `1px dashed ${Color(theme.palette.primary.main).alpha(0.5).string()}`,
  },
  dateRangeContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr auto',
    columnGap: '0.25rem',
  },
}));

const CustomsInstructionInsuranceOptions: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { form, savedInsuranceRef } = useContext(CustomsInstructionContext);
  const { fields } = form.root;

  return (
    <>
      <ManagementFormLabel field={fields.insurance} required />

      <InstructionInputContainer>
        <CardCheckbox
          field={fields.insurance.discriminatorField}
          value="INDIVIDUAL"
          label={
            <span className={classes.insuranceOption}>
              {instructionStrings.preferIndividualInsurance}
            </span>
          }
          size="small"
          onSelected={() => {
            const saved = savedInsuranceRef.current.find((d) => d.tag === 'INDIVIDUAL');
            if (saved) {
              fields.insurance.setOutput(saved);
            }
          }}
        />
        <CardCheckbox
          field={fields.insurance.discriminatorField}
          value="BLANKET"
          label={
            <span className={classes.insuranceOption}>
              {instructionStrings.preferBlanketInsurance}
            </span>
          }
          size="small"
          onSelected={() => {
            const saved = savedInsuranceRef.current.find((d) => d.tag === 'BLANKET');
            if (saved) {
              fields.insurance.setOutput(saved);
            }
          }}
        />
        <CardCheckbox
          field={fields.insurance.discriminatorField}
          value="NA"
          label={
            <span className={classes.insuranceOption}>{instructionStrings.preferNoInsurance}</span>
          }
          size="small"
          onSelected={async () => {}}
        />
      </InstructionInputContainer>
    </>
  );
};

const IndividualInsuranceFilesInput: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isAdmin = useIsAdmin();
  const { form, isEditing } = useContext(CustomsInstructionContext);
  const { fields } = form.root;
  const { insurance } = fields;
  const { fieldsResult } = insurance;

  const value = getDecodeResult(insurance.decodeResult);
  if (!value) {
    return null;
  }

  // Auto-save individual insurance files
  // because staff are allowed to edit them even when "not editing"
  useEffect(() => {
    if (
      isAdmin &&
      value.tag === 'INDIVIDUAL' &&
      !isEditing &&
      fieldsResult.success &&
      fieldsResult.fields.discriminator === 'INDIVIDUAL' &&
      fieldsResult.fields.individual_insurance_files.touched
    ) {
      form.root.fields.action.setOutput('save');
      form.element?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  }, [value, value?.tag === 'INDIVIDUAL' && value?.individual_insurance_files.length]);

  // Don't display anything for the client, when the files haven't been uploaded by staff yet
  if (
    !fieldsResult.success ||
    fieldsResult.fields.discriminator !== 'INDIVIDUAL' ||
    value.tag !== 'INDIVIDUAL' ||
    (!isAdmin && !value.individual_insurance_files?.length)
  ) {
    return null;
  }

  const insuranceFields = fieldsResult.fields;

  return (
    <>
      <ManagementFormLabel field={insuranceFields.individual_insurance_files} />

      <InstructionInputContainer>
        <FileArrayInput
          field={insuranceFields.individual_insurance_files}
          buttonProps={{
            color: 'outlined',
            className: classes.selfStart,
            text: formStrings.selectFiles,
          }}
          readOnly={!isAdmin}
        />
        {isAdmin ? (
          <InstructionHint>{strings.individualInsuranceFilesHint}</InstructionHint>
        ) : (
          <InstructionHint>{strings.individualInsuranceFilesClientHint}</InstructionHint>
        )}
      </InstructionInputContainer>
    </>
  );
});

const CustomsInstructionInsuranceInputs: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { form, savedInsuranceRef, user } = useContext(CustomsInstructionContext);
  const { fields } = form.root;
  const { insurance } = fields;
  const { fieldsResult } = insurance;
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    const value = getDecodeResult(insurance.decodeResult);
    if (value) {
      savedInsuranceRef.current = [
        ...savedInsuranceRef.current.filter((c) => c.tag !== value.tag),
        value,
      ];
    }
  }, [insurance.decodeResult]);

  if (fieldsResult.success) {
    const { fields } = fieldsResult;
    switch (fields.discriminator) {
      case 'INDIVIDUAL':
        return <IndividualInsuranceFilesInput />;
      case 'BLANKET':
        return (
          <>
            <ManagementFormLabelInner required>
              {strings.blanketInsuranceInfo}
            </ManagementFormLabelInner>
            <InstructionInputContainer>
              {pipe(unwrapDecodeResult(insurance.decodeResult) as BlanketInsuranceBase, (value) => (
                <>
                  <BlanketInsuranceFilter
                    open={filterOpen}
                    value={value}
                    onClose={(v) => {
                      v &&
                        insurance.setOutput({
                          tag: 'BLANKET',
                          ...v,
                        });
                      setFilterOpen(false);
                    }}
                    userId={user.id}
                  />

                  {value.company && (
                    <div className={classes.backgroundGray50} style={{ padding: 20 }}>
                      <BlanketInsuranceDisplay value={value} />
                    </div>
                  )}

                  <Button
                    color="outlined"
                    size="small"
                    type="button"
                    text={strings.selectBlanketInsuranceInfo}
                    className={classes.selfStart}
                    onClick={() => setFilterOpen(true)}
                  />
                </>
              ))}

              <ErrorMessages field={fields.company} />
            </InstructionInputContainer>
          </>
        );
      case 'NA':
        return null;
    }
  }
  return null;
});

const BlanketInsuranceDisplay: React.FC<{ value: BlanketInsuranceBase }> = ({ value }) => {
  const classes = {
    ...useStyles(),
    ...useCommonStyles(),
  };

  return (
    <div className={classes.insuranceDisplayGrid}>
      <div>{instructionStrings.insuranceCompany}</div>
      <div>{value.company || '-'}</div>

      <div>{instructionStrings.insurancePolicyNo}</div>
      <div>{value.policy_no || '-'}</div>

      <div>{instructionStrings.insurancePeriod}</div>
      <div>
        {value.start ? calendarStrings.yearMonthDay(new Date(value.start)) : '?'}
        {' - '}
        {value.end ? calendarStrings.yearMonthDay(new Date(value.end)) : '?'}
      </div>

      <div>{instructionStrings.additionalFiles}</div>
      <div className={classes.filesContainer}>
        {value.blanket_insurance_files.length === 0 && '-'}
        {value.blanket_insurance_files.map(({ url, name }) => (
          <FileLink key={url} href={url} variant="gray-block">
            {name}
          </FileLink>
        ))}
      </div>
    </div>
  );
};

const CustomsInstructionInsuranceDisplay: React.FC = observer(() => {
  const classes = {
    ...useStyles(),
    ...useCommonStyles(),
  };

  const { form } = useContext(CustomsInstructionContext);
  const { fields } = form.root;
  const { insurance } = fields;

  const value = unwrapDecodeResult(insurance.decodeResult);

  const title = useMemo(() => {
    switch (value.tag) {
      case 'INDIVIDUAL':
        return instructionStrings.preferIndividualInsurance;
      case 'BLANKET':
        return instructionStrings.preferBlanketInsurance;
      case 'NA':
        return instructionStrings.preferNoInsurance;
    }
  }, [value]);

  const content = useMemo(() => {
    switch (value.tag) {
      case 'INDIVIDUAL':
        return null;
      case 'BLANKET':
        return <BlanketInsuranceDisplay value={value} />;
      case 'NA':
        return null;
    }
  }, [value]);

  return (
    <>
      <ManagementFormLabel field={fields.insurance} required />
      <InstructionInputContainer>
        <div className={classes.insuranceDisplay}>
          <div className={classes.insuranceDisplayTitle}>{title}</div>
          {content && <div className={classes.split} />}
          {content}
        </div>
      </InstructionInputContainer>

      <IndividualInsuranceFilesInput />
    </>
  );
});

export const CustomsInstructionInsurance: React.FC = () => {
  const { isEditing } = useContext(CustomsInstructionContext);

  return (
    <>
      {isEditing && <CustomsInstructionInsuranceOptions />}
      {isEditing && <CustomsInstructionInsuranceInputs />}
      {!isEditing && <CustomsInstructionInsuranceDisplay />}
    </>
  );
};
